import {
    AlarmClockOff,
    Ban,
    BadgeCheck,
    BarChart,
    BookOpen,
    Bot,
    Boxes,
    Briefcase,
    Building2,
    CalendarCheck,
    CalendarClock,
    Check,
    CircleOff,
    ClipboardList,
    Clock,
    CreditCard,
    Crown,
    Database,
    Eye,
    FileText,
    Headphones,
    Image,
    Inbox,
    KeyRound,
    Layers,
    LayoutDashboard,
    Link,
    Link2,
    ListChecks,
    ListOrdered,
    Mail,
    Megaphone,
    MessageSquareText,
    Mic,
    Pencil,
    Phone,
    PhoneCall,
    Plus,
    Radio,
    Settings2,
    ShieldCheck,
    Target,
    Ticket,
    Handshake,
    Trash2,
    UserCog,
    UserPlus,
    Users,
    UsersRound,
    Webhook,
    Workflow,
    Wrench,
    X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { TagPlusIcon } from '@/components/ui/tag';

import type { CrudAction, InvitationStatus } from './interfaces';

export const SYSTEM_ROLE_NAME = {
    OWNER: 'OWNER',
    ADMIN: 'ADMIN',
    MANAGER: 'MANAGER',
    AGENT: 'AGENT',
    VIEWER: 'VIEWER',
} as const;

export type SystemRoleName = (typeof SYSTEM_ROLE_NAME)[keyof typeof SYSTEM_ROLE_NAME];

export const CRUD_ACTIONS: readonly CrudAction[] = ['create', 'read', 'update', 'delete'];

/**
 * Frontend permission constants — mirrors the backend `Permission` enum
 * (`backend/src/common/constants/permissions.ts`).
 *
 * Use these instead of raw strings in `<PermissionGate>` and `usePermissions`
 * calls so typos are caught at compile time and IDEs can autocomplete.
 *
 * When a new permission is added to the backend catalog, add it here too.
 */
export const PERMISSION = {
    // ── Campaigns ──────────────────────────────────────────────────────────
    CAMPAIGNS_CREATE: 'campaigns:create',
    CAMPAIGNS_READ: 'campaigns:read',
    CAMPAIGNS_UPDATE: 'campaigns:update',
    CAMPAIGNS_DELETE: 'campaigns:delete',
    CAMPAIGNS_SEND: 'campaigns:send',
    CAMPAIGNS_PAUSE: 'campaigns:pause',
    CAMPAIGNS_CANCEL: 'campaigns:cancel',
    CAMPAIGNS_END_RECURRENCE: 'campaigns:end_recurrence',

    // ── Contacts ───────────────────────────────────────────────────────────
    CONTACTS_CREATE: 'contacts:create',
    CONTACTS_READ: 'contacts:read',
    CONTACTS_UPDATE: 'contacts:update',
    CONTACTS_DELETE: 'contacts:delete',
    CONTACTS_IMPORT: 'contacts:import',

    // ── Segments ───────────────────────────────────────────────────────────
    SEGMENTS_CREATE: 'segments:create',
    SEGMENTS_READ: 'segments:read',
    SEGMENTS_UPDATE: 'segments:update',
    SEGMENTS_DELETE: 'segments:delete',

    // ── Tags ───────────────────────────────────────────────────────────────
    TAGS_CREATE: 'tags:create',
    TAGS_READ: 'tags:read',
    TAGS_UPDATE: 'tags:update',
    TAGS_DELETE: 'tags:delete',

    // ── Quick Messages (inbox canned replies) ──────────────────────────────
    QUICK_MESSAGES_CREATE: 'quick-messages:create',
    QUICK_MESSAGES_READ: 'quick-messages:read',
    QUICK_MESSAGES_UPDATE: 'quick-messages:update',
    QUICK_MESSAGES_DELETE: 'quick-messages:delete',

    // ── Inbox ──────────────────────────────────────────────────────────────
    INBOX_READ: 'inbox:read',
    INBOX_REPLY: 'inbox:reply',
    INBOX_ASSIGN: 'inbox:assign',
    INBOX_CLOSE: 'inbox:close',

    // ── Phone Numbers ──────────────────────────────────────────────────────
    PHONE_NUMBERS_READ: 'phone-numbers:read',
    PHONE_NUMBERS_MANAGE: 'phone-numbers:manage',

    // ── Channels ───────────────────────────────────────────────────────────
    CHANNELS_READ: 'channels:read',
    CHANNELS_MANAGE: 'channels:manage',

    // ── Settings (opt-out / opt-in / SMS templates) ─────────────────────
    SETTINGS_READ: 'settings:read',
    SETTINGS_UPDATE: 'settings:update',

    // ── Organization profile ───────────────────────────────────────────────
    ORGANIZATION_CREATE: 'organization:create',
    ORGANIZATION_READ: 'organization:read',
    ORGANIZATION_UPDATE: 'organization:update',

    // ── Users ──────────────────────────────────────────────────────────────
    USERS_CREATE: 'users:create',
    USERS_READ: 'users:read',
    USERS_UPDATE: 'users:update',
    USERS_DELETE: 'users:delete',

    // ── Roles ──────────────────────────────────────────────────────────────
    ROLES_CREATE: 'roles:create',
    ROLES_READ: 'roles:read',
    ROLES_UPDATE: 'roles:update',
    ROLES_DELETE: 'roles:delete',

    // ── Billing ────────────────────────────────────────────────────────────
    BILLING_READ: 'billing:read',
    BILLING_MANAGE: 'billing:manage',

    // ── Analytics / Dashboard ──────────────────────────────────────────────
    ANALYTICS_READ: 'analytics:read',
    DASHBOARD_READ: 'dashboard:read',
    // Workspace-level dashboard administration (set the workspace default).
    // Owner/Admin only via the backend's ALL_PERMISSIONS grant; deliberately
    // not exposed as a matrix column for custom roles.
    DASHBOARD_MANAGE: 'dashboard:manage',

    // ── Join Links ─────────────────────────────────────────────────────────
    JOIN_LINKS_CREATE: 'join-links:create',
    JOIN_LINKS_READ: 'join-links:read',
    JOIN_LINKS_UPDATE: 'join-links:update',
    JOIN_LINKS_DELETE: 'join-links:delete',

    // ── Lead Forms ─────────────────────────────────────────────────────────
    LEAD_FORMS_CREATE: 'lead-forms:create',
    LEAD_FORMS_READ: 'lead-forms:read',
    LEAD_FORMS_UPDATE: 'lead-forms:update',
    LEAD_FORMS_DELETE: 'lead-forms:delete',

    // ── Short Links ────────────────────────────────────────────────────────
    SHORT_LINKS_CREATE: 'short-links:create',
    SHORT_LINKS_READ: 'short-links:read',
    SHORT_LINKS_UPDATE: 'short-links:update',
    SHORT_LINKS_DELETE: 'short-links:delete',

    // ── Join Keywords ──────────────────────────────────────────────────────
    JOIN_KEYWORDS_CREATE: 'join-keywords:create',
    JOIN_KEYWORDS_READ: 'join-keywords:read',
    JOIN_KEYWORDS_UPDATE: 'join-keywords:update',
    JOIN_KEYWORDS_DELETE: 'join-keywords:delete',

    // ── Polls ──────────────────────────────────────────────────────────────
    POLLS_CREATE: 'polls:create',
    POLLS_READ: 'polls:read',
    POLLS_UPDATE: 'polls:update',
    POLLS_DELETE: 'polls:delete',
    POLLS_MANAGE: 'polls:manage',

    // ── SMS Templates ──────────────────────────────────────────────────────
    SMS_TEMPLATES_CREATE: 'sms-templates:create',
    SMS_TEMPLATES_READ: 'sms-templates:read',
    SMS_TEMPLATES_UPDATE: 'sms-templates:update',
    SMS_TEMPLATES_DELETE: 'sms-templates:delete',

    // ── WhatsApp Templates ────────────────────────────────────────────────
    WHATSAPP_TEMPLATES_CREATE: 'whatsapp-templates:create',
    WHATSAPP_TEMPLATES_READ: 'whatsapp-templates:read',
    WHATSAPP_TEMPLATES_UPDATE: 'whatsapp-templates:update',
    WHATSAPP_TEMPLATES_DELETE: 'whatsapp-templates:delete',

    // ── Email Templates ───────────────────────────────────────────────────
    EMAIL_TEMPLATES_CREATE: 'email-templates:create',
    EMAIL_TEMPLATES_READ: 'email-templates:read',
    EMAIL_TEMPLATES_UPDATE: 'email-templates:update',
    EMAIL_TEMPLATES_DELETE: 'email-templates:delete',

    // ── Media Assets ───────────────────────────────────────────────────────
    MEDIA_ASSETS_CREATE: 'media-assets:create',
    MEDIA_ASSETS_READ: 'media-assets:read',
    MEDIA_ASSETS_DELETE: 'media-assets:delete',

    // ── Opt-Out (keywords + reply message) ─────────────────────────────────
    OPT_OUT_CREATE: 'opt-out:create',
    OPT_OUT_READ: 'opt-out:read',
    OPT_OUT_UPDATE: 'opt-out:update',
    OPT_OUT_DELETE: 'opt-out:delete',

    // ── Opt-In (keywords + reply message) ──────────────────────────────────
    OPT_IN_CREATE: 'opt-in:create',
    OPT_IN_READ: 'opt-in:read',
    OPT_IN_UPDATE: 'opt-in:update',
    OPT_IN_DELETE: 'opt-in:delete',

    // ── Scheduling: Meeting Types ──────────────────────────────────────────
    MEETING_TYPES_CREATE: 'meeting-types:create',
    MEETING_TYPES_READ: 'meeting-types:read',
    MEETING_TYPES_UPDATE: 'meeting-types:update',
    MEETING_TYPES_DELETE: 'meeting-types:delete',

    // ── Scheduling: Bookings ───────────────────────────────────────────────
    BOOKINGS_CREATE: 'bookings:create',
    BOOKINGS_READ: 'bookings:read',
    BOOKINGS_UPDATE: 'bookings:update',
    BOOKINGS_DELETE: 'bookings:delete',

    // ── Scheduling: Availability ───────────────────────────────────────────
    AVAILABILITY_MANAGE: 'availability:manage',

    // ── Scheduling: Calendar ───────────────────────────────────────────────
    CALENDAR_READ: 'calendar:read',
    CALENDAR_INTEGRATIONS_MANAGE: 'calendar-integrations:manage',

    // ── Field service (Jobber / JobNimbus) ─────────────────────────────────
    FIELD_SERVICE_READ: 'field-service:read',
    FIELD_SERVICE_MANAGE: 'field-service:manage',

    // ── API Keys ───────────────────────────────────────────────────────────
    API_KEYS_READ: 'api-keys:read',
    API_KEYS_MANAGE: 'api-keys:manage',

    // ── Webhooks ───────────────────────────────────────────────────────────
    WEBHOOKS_READ: 'webhooks:read',
    WEBHOOKS_MANAGE: 'webhooks:manage',

    // ── Module Builder ─────────────────────────────────────────────────────
    MODULES_CREATE: 'modules:create',
    MODULES_READ: 'modules:read',
    MODULES_UPDATE: 'modules:update',
    MODULES_DELETE: 'modules:delete',
    MODULE_SECTIONS_CREATE: 'module-sections:create',
    MODULE_SECTIONS_UPDATE: 'module-sections:update',
    MODULE_SECTIONS_DELETE: 'module-sections:delete',
    MODULE_FIELDS_CREATE: 'module-fields:create',
    MODULE_FIELDS_UPDATE: 'module-fields:update',
    MODULE_FIELDS_DELETE: 'module-fields:delete',
    MODULE_RECORDS_CREATE: 'module-records:create',
    MODULE_RECORDS_READ: 'module-records:read',
    MODULE_RECORDS_UPDATE: 'module-records:update',
    MODULE_RECORDS_DELETE: 'module-records:delete',

    // ── Tickets ────────────────────────────────────────────────────────────
    TICKETS_CREATE: 'tickets:create',
    TICKETS_READ: 'tickets:read',
    TICKETS_UPDATE: 'tickets:update',
    TICKETS_DELETE: 'tickets:delete',
    TICKETS_COMMENT: 'tickets:comment',

    // ── Opportunities & Pipelines ──────────────────────────────────────────
    OPPORTUNITIES_CREATE: 'opportunities:create',
    OPPORTUNITIES_READ: 'opportunities:read',
    OPPORTUNITIES_UPDATE: 'opportunities:update',
    OPPORTUNITIES_DELETE: 'opportunities:delete',
    OPPORTUNITIES_COMMENT: 'opportunities:comment',
    PIPELINES_MANAGE: 'pipelines:manage',

    // ── Voice — calls (softphone) ────────────────────────────────────────
    VOICE_CALLS_MAKE: 'voice-calls:make',
    VOICE_CALLS_RECEIVE: 'voice-calls:receive',
    VOICE_CALLS_READ: 'voice-calls:read',
    VOICE_CALLS_MANAGE: 'voice-calls:manage',

    // ── Voice — per-number configuration ──────────────────────────────────
    VOICE_CONFIG_READ: 'voice-config:read',
    VOICE_CONFIG_MANAGE: 'voice-config:manage',

    // ── Voice — recordings & voicemails ───────────────────────────────────
    VOICE_RECORDINGS_LISTEN: 'voice-recordings:listen',
    VOICE_RECORDINGS_DELETE: 'voice-recordings:delete',

    // ── Advertising ────────────────────────────────────────────────────────
    ADVERTISING_READ: 'advertising:read',
    ADVERTISING_MANAGE: 'advertising:manage',

    // ── Sequences ──────────────────────────────────────────────────────────
    SEQUENCES_CREATE: 'sequences:create',
    SEQUENCES_READ: 'sequences:read',
    SEQUENCES_UPDATE: 'sequences:update',
    SEQUENCES_DELETE: 'sequences:delete',
    SEQUENCES_PUBLISH: 'sequences:publish',
    SEQUENCES_EXECUTE: 'sequences:execute',

    // ── Workflows ──────────────────────────────────────────────────────────
    WORKFLOWS_CREATE: 'workflows:create',
    WORKFLOWS_READ: 'workflows:read',
    WORKFLOWS_UPDATE: 'workflows:update',
    WORKFLOWS_DELETE: 'workflows:delete',
    WORKFLOWS_PUBLISH: 'workflows:publish',
    WORKFLOWS_EXECUTE: 'workflows:execute',

    // ── AI Employees ───────────────────────────────────────────────────────
    AI_AGENTS_CREATE: 'ai-agents:create',
    AI_AGENTS_READ: 'ai-agents:read',
    AI_AGENTS_UPDATE: 'ai-agents:update',
    AI_AGENTS_DELETE: 'ai-agents:delete',
    /** Model/prompt configuration — platform-staff-only, never in a default role. */
    AI_EMPLOYEE_ADVANCED: 'ai-employees:advanced',
    AI_CONVERSATIONS_CONTROL: 'ai-conversations:control',
    AI_HANDOVER_RULES_READ: 'ai-handover-rules:read',
    AI_HANDOVER_RULES_MANAGE: 'ai-handover-rules:manage',
    AI_RUNS_READ: 'ai-runs:read',

    // ── Knowledge Bases ────────────────────────────────────────────────────
    KNOWLEDGE_BASES_CREATE: 'knowledge-bases:create',
    KNOWLEDGE_BASES_READ: 'knowledge-bases:read',
    KNOWLEDGE_BASES_UPDATE: 'knowledge-bases:update',
    KNOWLEDGE_BASES_DELETE: 'knowledge-bases:delete',

    // ── Website Chat (chat widget settings) ────────────────────────────────
    CHAT_WIDGET_READ: 'chat-widget:read',
    CHAT_WIDGET_UPDATE: 'chat-widget:update',

    // ── Marketplace (Template Library) ──────────────────────────────────────
    MARKETPLACE_READ: 'marketplace:read',
} as const;

/** Union of every valid permission string — use as prop type for PermissionGate. */
export type Permission = (typeof PERMISSION)[keyof typeof PERMISSION];

export const USERS_ROLES_QUERY_KEYS = {
    MEMBERS: ['members'] as const,
    ROLES: ['roles'] as const,
    ROLE_BY_ID: (id: string) => ['roles', id] as const,
    ROLE_MEMBERS: (id: string) => ['roles', id, 'members'] as const,
    PERMISSIONS: ['permissions'] as const,
    MY_PERMISSIONS: ['permissions', 'me'] as const,
    INVITATIONS: ['invitations'] as const,
    INVITATION_VERIFY_PUBLIC: (token: string) =>
        ['invitations', 'public', 'verify', token] as const,
};

export const ROLE_ICON_BY_NAME: Record<string, LucideIcon> = {
    OWNER: Crown,
    ADMIN: ShieldCheck,
    MANAGER: Briefcase,
    AGENT: Headphones,
    VIEWER: Eye,
};

export const ROLE_BADGE_VARIANT_BY_NAME: Record<
    string,
    'primary' | 'success' | 'info' | 'warning' | 'danger' | 'default'
> = {
    OWNER: 'primary',
    ADMIN: 'danger',
    MANAGER: 'info',
    AGENT: 'success',
    VIEWER: 'default',
};

export const DEFAULT_ROLE_ICON: LucideIcon = UserCog;
export const DEFAULT_ROLE_BADGE_VARIANT = 'default' as const;

/**
 * Modules shown in the role permission matrix UI, grouped for display.
 *
 * Must stay aligned with the backend's `MATRIX_MODULES`
 * (`backend/src/common/constants/permission-expansion.ts`) — including order.
 * A row here that the backend does not know about renders a toggle it will
 * never persist; a backend row missing here is ungrantable, and because
 * saving a role rewrites its whole grant set from this grid, any grant the
 * role already held for that module is silently dropped. Asserted across the
 * two packages by `permissions.spec.ts`.
 *
 * Resources absent from this list are covered by a parent row: sections and
 * fields by `modules`, availability by `meeting-types`, calendar and calendar
 * integrations by `bookings`, AI conversations by `inbox`, AI runs and handover
 * rules by `ai-agents`.
 */
export const MATRIX_MODULE_GROUPS: readonly {
    readonly key: string;
    readonly resources: readonly string[];
}[] = [
    { key: 'overview', resources: ['dashboard', 'analytics'] },
    {
        key: 'messaging',
        resources: [
            'campaigns',
            'inbox',
            'sms-templates',
            'whatsapp-templates',
            'email-templates',
            'quick-messages',
            'media-assets',
            'chat-widget',
            'marketplace',
        ],
    },
    {
        key: 'audience',
        resources: [
            'contacts',
            'segments',
            'tags',
            'join-links',
            'join-keywords',
            'lead-forms',
            'short-links',
            'opt-out',
            'opt-in',
        ],
    },
    { key: 'automation', resources: ['workflows', 'sequences', 'polls'] },
    { key: 'ai', resources: ['ai-agents', 'knowledge-bases'] },
    { key: 'voice', resources: ['voice-calls', 'voice-config', 'voice-recordings'] },
    { key: 'scheduling', resources: ['meeting-types', 'bookings'] },
    { key: 'support', resources: ['tickets'] },
    { key: 'sales', resources: ['opportunities'] },
    { key: 'data', resources: ['modules', 'module-records'] },
    {
        key: 'admin',
        resources: [
            'users',
            'roles',
            'organization',
            'billing',
            'channels',
            'phone-numbers',
            'api-keys',
            'webhooks',
            'advertising',
            'field-service',
        ],
    },
];

/** Flat ordered row list, derived so grouping and order cannot drift apart. */
export const MATRIX_MODULES: readonly string[] = MATRIX_MODULE_GROUPS.flatMap(
    (group) => group.resources,
);

/** Section headings for {@link MATRIX_MODULE_GROUPS}. */
export const MATRIX_GROUP_LABELS: Record<string, string> = {
    overview: 'Overview',
    messaging: 'Messaging',
    audience: 'Audience',
    automation: 'Automation',
    ai: 'AI',
    voice: 'Voice',
    scheduling: 'Scheduling',
    support: 'Support',
    sales: 'Sales',
    data: 'Data',
    admin: 'Administration',
};

/**
 * Row labels for resources whose key does not Title Case well ("Ai Agents",
 * "Api Keys") or where the product name differs from the permission key.
 * Anything absent falls back to Title Case in the matrix component.
 */
export const MATRIX_MODULE_LABELS: Record<string, string> = {
    'ai-agents': 'AI Employees',
    'api-keys': 'API Keys',
    'sms-templates': 'SMS Templates',
    'whatsapp-templates': 'WhatsApp Templates',
    'lead-forms': 'Forms',
    'field-service': 'Field Service',
    'opt-in': 'Opt-In',
    'opt-out': 'Opt-Out',
};

export const PERMISSION_RESOURCE_ICONS: Record<string, LucideIcon> = {
    campaigns: Megaphone,
    contacts: Users,
    // `segments` and `polls` match the icons used by the main app sidebar
    // (Layers + ListChecks) so the permission matrix reads as the same
    // visual vocabulary the rest of the app already uses.
    segments: Layers,
    tags: TagPlusIcon,
    inbox: Inbox,
    'phone-numbers': Phone,
    channels: Radio,
    users: UserPlus,
    billing: CreditCard,
    analytics: BarChart,
    dashboard: LayoutDashboard,
    'join-links': Link2,
    polls: ListChecks,
    roles: ShieldCheck,
    'sms-templates': FileText,
    'whatsapp-templates': MessageSquareText,
    organization: Building2,
    'opt-out': CircleOff,
    'opt-in': BadgeCheck,
    modules: Boxes,
    'module-records': Database,
    tickets: Ticket,
    opportunities: Handshake,
    'voice-calls': PhoneCall,
    'voice-config': Settings2,
    'voice-recordings': Mic,
    // Automation & AI, scheduling and admin rows — icons match the app sidebar
    // so the matrix reads as the same visual vocabulary as the nav.
    workflows: Workflow,
    sequences: ListOrdered,
    'ai-agents': Bot,
    'knowledge-bases': BookOpen,
    'lead-forms': ClipboardList,
    'join-keywords': MessageSquareText,
    'short-links': Link,
    'email-templates': Mail,
    'quick-messages': MessageSquareText,
    'media-assets': Image,
    'meeting-types': CalendarClock,
    bookings: CalendarCheck,
    'api-keys': KeyRound,
    webhooks: Webhook,
    advertising: Target,
    'field-service': Wrench,
};

export const ACTION_ICONS: Record<CrudAction, LucideIcon> = {
    create: Plus,
    read: Eye,
    update: Pencil,
    delete: Trash2,
};

export const INVITATION_STATUS_BADGE: Record<
    InvitationStatus,
    {
        variant: 'primary' | 'success' | 'info' | 'warning' | 'danger' | 'default';
        icon: LucideIcon;
    }
> = {
    PENDING: { variant: 'warning', icon: Clock },
    ACCEPTED: { variant: 'success', icon: Check },
    DECLINED: { variant: 'danger', icon: X },
    REVOKED: { variant: 'default', icon: Ban },
    EXPIRED: { variant: 'default', icon: AlarmClockOff },
};

export const USER_MANAGEMENT_ICONS = {
    USERS: UsersRound,
    INVITATIONS: Inbox,
    ROLES: ShieldCheck,
} as const;

/**
 * For modules whose CRUD slot is intentionally unavailable in the catalog —
 * the matrix UI renders these as disabled placeholders so the grid layout
 * stays aligned (4 columns per row).
 */
export const AVAILABLE_ACTIONS_BY_MODULE: Record<string, readonly CrudAction[]> = {
    dashboard: ['read'],
    campaigns: ['create', 'read', 'update', 'delete'],
    contacts: ['create', 'read', 'update', 'delete'],
    segments: ['create', 'read', 'update', 'delete'],
    tags: ['create', 'read', 'update', 'delete'],
    inbox: ['read', 'update'],
    polls: ['create', 'read', 'update', 'delete'],
    'join-links': ['create', 'read', 'update', 'delete'],
    'sms-templates': ['create', 'read', 'update', 'delete'],
    'whatsapp-templates': ['create', 'read', 'update', 'delete'],
    channels: ['read', 'update'],
    users: ['create', 'read', 'update', 'delete'],
    roles: ['create', 'read', 'update', 'delete'],
    // `organization` exposes read/update only — create is system-level (not
    // per-organization), so it renders as a dot placeholder in the matrix.
    // `delete` is intentionally omitted — there is no `organization:delete`
    // permission in the catalog.
    organization: ['read', 'update'],
    'opt-out': ['create', 'read', 'update', 'delete'],
    'opt-in': ['create', 'read', 'update', 'delete'],
    // `billing:manage` is not a CRUD column, so — exactly like `channels`,
    // `inbox` and `voice-*` above — `update` is its surrogate and the backend
    // expands the update bit into `billing:manage`. Listing the raw `manage`
    // action here instead made the Update cell render as a disabled dot, which
    // left `billing:manage` ungrantable to any custom role.
    billing: ['read', 'update'],
    // Section/field builder permissions are implied by modules create+update
    // (see backend permission-expansion.ts) — one matrix row covers all three.
    modules: ['create', 'read', 'update', 'delete'],
    'module-records': ['create', 'read', 'update', 'delete'],
    tickets: ['create', 'read', 'update', 'delete'],
    // `pipelines:manage` and `opportunities:comment` are derived from this row
    // (see backend permission-expansion.ts) — one matrix row covers both.
    opportunities: ['create', 'read', 'update', 'delete'],
    // voice-calls/voice-config have no create permission in the catalog —
    // `update` is the surrogate for "manage" (make/receive/manage calls;
    // manage number config), same shape as `channels`/`inbox`.
    'voice-calls': ['read', 'update'],
    'voice-config': ['read', 'update'],
    // voice-recordings has no `:read` permission (only listen + delete) —
    // `update` is the surrogate for "listen", `delete` maps directly.
    'voice-recordings': ['update', 'delete'],
    analytics: ['read'],
    workflows: ['create', 'read', 'update', 'delete'],
    sequences: ['create', 'read', 'update', 'delete'],
    // Publish and execute are not CRUD columns: publish comes with create+update
    // and execute with update or delete (see backend permission-expansion.ts).
    'ai-agents': ['create', 'read', 'update', 'delete'],
    'knowledge-bases': ['create', 'read', 'update', 'delete'],
    'lead-forms': ['create', 'read', 'update', 'delete'],
    'join-keywords': ['create', 'read', 'update', 'delete'],
    'short-links': ['create', 'read', 'update', 'delete'],
    'email-templates': ['create', 'read', 'update', 'delete'],
    'quick-messages': ['create', 'read', 'update', 'delete'],
    // No `media-assets:update` in the catalog — renders as a dot placeholder.
    'media-assets': ['create', 'read', 'delete'],
    'meeting-types': ['create', 'read', 'update', 'delete'],
    bookings: ['create', 'read', 'update', 'delete'],
    // read/manage modules — `update` is the surrogate for "manage", same shape
    // as channels/inbox/billing above.
    'phone-numbers': ['read', 'update'],
    'api-keys': ['read', 'update'],
    webhooks: ['read', 'update'],
    advertising: ['read', 'update'],
    'field-service': ['read', 'update'],
};

/** Members fetched per page in the infinite query. */
export const MEMBERS_PAGE_SIZE = 24;
export const INVITATIONS_PAGE_SIZE = 24;
